import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { 
  setToken, 
  removeToken, 
  getToken, 
  validateToken, 
  getUserData, 
  setUserData, 
  validateUserData,
  clearAuthData,
  logAuthState,
  determineUserType 
} from '../utils/authUtils';
import { AUTH_CONFIG, STORAGE_KEYS, ERROR_MESSAGES } from '../config/constants';

// Create the AuthContext
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authState, setAuthState] = useState({
    initialized: false,
    tokenValid: false,
    tokenExpiringSoon: false,
    lastChecked: null
  });

  // Listen for profile updates from ProfileSettingsPage
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      const updatedUser = event.detail;

      setUser(prevUser => ({ ...prevUser, ...updatedUser }));
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);

  // Check for token and initialize auth state on load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Skip initialization on admin routes - admins use separate adminToken
        if (window.location.pathname.startsWith('/admin')) {
          logAuthState('INIT_SKIP_ADMIN_ROUTE');
          setLoading(false);
          setAuthState({
            initialized: true,
            tokenValid: false,
            tokenExpiringSoon: false,
            lastChecked: new Date().toISOString()
          });
          return;
        }
        
        setLoading(true);
        logAuthState('INIT_START');
        
        // Get and validate token
        const token = getToken();
        const tokenValidation = validateToken(token);
        
        if (tokenValidation.valid) {
          // Token is valid, get user data
          let userData = getUserData();
          
          // If we have a valid token but no user data, try to extract from token
          if (!userData && tokenValidation.decoded) {
            const decoded = tokenValidation.decoded;
            userData = {
              id: decoded.sub || decoded.uid || decoded.id,
              userType: decoded.userType || determineUserType(decoded),
              profileCompleted: decoded.profileCompleted === undefined ? false : decoded.profileCompleted,
              name: decoded.name,
              email: decoded.email,
              contactNumber: decoded.contactNumber || decoded.phone,
              isPhoneVerified: true // If we have a valid token from Firebase, phone is verified
            };
            
            // Store the extracted user data
            setUserData(userData);
            logAuthState('USER_DATA_EXTRACTED_FROM_TOKEN', { userData });
          }
          
          // Validate and repair user data if needed
          // If user data is missing, reconstruct it from the valid token
          if (!userData && tokenValidation.decoded) {
            console.warn('[WARNING] User data missing from storage, reconstructing from valid token.');
            userData = {
              id: tokenValidation.decoded.sub || tokenValidation.decoded.uid || tokenValidation.decoded.id,
              userType: tokenValidation.decoded.userType || determineUserType(tokenValidation.decoded),
              profileCompleted: tokenValidation.decoded.profileCompleted === undefined ? false : tokenValidation.decoded.profileCompleted,
              name: tokenValidation.decoded.name,
              email: tokenValidation.decoded.email
            };
            logAuthState('USER_DATA_RECONSTRUCTED', { source: 'token' });
          }

          // Validate and repair user data if needed
          if (userData) {
            const validatedUser = validateUserData(userData);
            setUserData(validatedUser); // Persist the repaired/reconstructed data

            // Set auth state
            setUser(validatedUser);
            setIsAuthenticated(true);
            setAuthState({
              initialized: true,
              tokenValid: true,
              tokenExpiringSoon: tokenValidation.expiringSoon,
              lastChecked: new Date().toISOString()
            });
            
            logAuthState('INIT_SUCCESS', { 
              userType: validatedUser.userType,
              profileCompleted: validatedUser.profileCompleted
            });
          } else {
            // This should now be a rare case where token is valid but contains no user info
            logAuthState('INIT_TOKEN_VALID_NO_USER_INFO');
            clearAuthData();
            setIsAuthenticated(false);
            setUser(null);
          }
        } else {
          // Token is invalid or missing
          if (tokenValidation.error) {
            logAuthState('INIT_TOKEN_INVALID', { error: tokenValidation.error });
          } else {
            logAuthState('INIT_NO_TOKEN');
          }
          
          // Check if we have user data without a valid token
          const userData = getUserData();
          if (userData) {
            // This is an inconsistent state - we have user data but no valid token
            logAuthState('INIT_INCONSISTENT_STATE', { 
              hasToken: false, 
              hasUserData: true 
            });
            
            // Clean up and reset state
            clearAuthData();
          }
          
          setIsAuthenticated(false);
          setUser(null);
          setAuthState({
            initialized: true,
            tokenValid: false,
            tokenExpiringSoon: false,
            lastChecked: new Date().toISOString()
          });
        }
      } catch (err) {
        // Handle initialization errors
        logAuthState('INIT_ERROR', { error: err.message });
        
        // Reset to a clean state
        setIsAuthenticated(false);
        setUser(null);
        setAuthState({
          initialized: true,
          tokenValid: false,
          tokenExpiringSoon: false,
          lastChecked: new Date().toISOString(),
          error: err.message
        });
        
        // Set error state
        setError('Failed to initialize authentication');
      } finally {
        setLoading(false);
      }
    };
    
    initializeAuth();
  }, []);

  // Method to set authenticated user directly from Firebase auth result
  // This function is async and callers must await it to ensure auth state is fully settled
  const setAuthenticatedUser = useCallback(async ({ token, user: userData }) => {
    setLoading(true);
    setError(null);
    
    try {
      logAuthState('SET_AUTH_USER_START', { 
        hasToken: !!token, 
        hasUserData: !!userData,
        userType: userData?.userType
      });
      
      // Validate inputs
      if (!token) {
        console.error('🚨 Authentication error: Token missing');
        throw new Error(ERROR_MESSAGES.AUTH.TOKEN_MISSING);
      }
      
      if (!userData) {
        console.error('🚨 Authentication error: User data missing');
        throw new Error('User data is required');
      }
      
      // Set token in storage with timestamp
      setToken(token);
      localStorage.setItem('authTokenCreatedAt', Date.now().toString());
      
      // IMPORTANT: Clear any existing admin tokens to prevent session conflicts
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      
      // Wait for localStorage to flush and ensure synchronous operations complete
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Validate token and extract claims
      const tokenValidation = validateToken(token);
      if (!tokenValidation.valid) {
        throw new Error(tokenValidation.error || ERROR_MESSAGES.AUTH.TOKEN_INVALID);
      }
      
      // Create a complete user object with defaults
      const userToStore = { 
        ...AUTH_CONFIG.DEFAULT_USER,
        ...userData 
      };
      
      // Validate userType
      const allowedUserTypes = ['candidate', 'employer', 'recruiter', 'affiliate'];
      if (userToStore.userType && !allowedUserTypes.includes(userToStore.userType)) {
        logAuthState('INVALID_USER_TYPE', { userType: userToStore.userType });
        throw new Error('Invalid user type');
      }

      // Extract additional data from token if available
      if (tokenValidation.decoded) {
        const decoded = tokenValidation.decoded;
        
        // Use token claims to fill in missing fields
        if (!userToStore.id) {
          userToStore.id = decoded.sub || decoded.uid || decoded.id;
        }
        
        if (!userToStore.email && decoded.email) {
          userToStore.email = decoded.email;
        }
        
        if (!userToStore.name && decoded.name) {
          userToStore.name = decoded.name;
        }
        
        // Only use token's userType if not explicitly provided
        if (!userToStore.userType && decoded.userType) {
          userToStore.userType = decoded.userType;
        }
        
        // Only use token's profileCompleted if not explicitly provided
        if (userToStore.profileCompleted === undefined && 
            decoded.profileCompleted !== undefined) {
          userToStore.profileCompleted = decoded.profileCompleted;
        }
      }
      
      // Critical validation - ensure we have a user type
      if (!userToStore.userType) {
        logAuthState('USER_TYPE_MISSING', { userData: userToStore });
        
        // Infer userType from URL only as last resort
        const path = window.location.pathname;
        if (path.includes('/employer')) {
          userToStore.userType = 'employer';
        } else if (path.includes('/candidate')) {
          userToStore.userType = 'candidate';
        } else if (path.includes('/recruiter')) {
          userToStore.userType = 'recruiter';
        }
        
        if (userToStore.userType) {
          console.warn(`[WARNING] UserType was inferred from URL, not from token or user data: ${userToStore.userType}`);
        } else {
          // Default to candidate if we can't determine
          userToStore.userType = 'candidate';
          logAuthState('USER_TYPE_DEFAULT_USED', { userType: 'candidate' });
        }
      }
      
      // Ensure boolean fields are properly typed
      userToStore.profileCompleted = !!userToStore.profileCompleted;
      userToStore.isEmailVerified = userToStore.isEmailVerified === undefined ? false : !!userToStore.isEmailVerified;
      userToStore.isPhoneVerified = userToStore.isPhoneVerified === undefined ? false : !!userToStore.isPhoneVerified;
      
      // Store user data in localStorage
      setUserData(userToStore);
      
      // Wait for user data to be persisted before proceeding
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Final validation check
      if (!userToStore.userType || !userToStore.id) {
        logAuthState('CRITICAL_DATA_MISSING', { userType: userToStore.userType, id: userToStore.id });
        throw new Error('Critical user data missing');
      }
      
      // Store user data
      setUser(userToStore);
      setIsAuthenticated(true);
      setAuthState({
        initialized: true,
        tokenValid: true,
        tokenExpiringSoon: tokenValidation.expiringSoon || false,
        lastChecked: new Date().toISOString()
      });
      
      // Wait for React's state updates to propagate before resolving
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Debug: Verify the user was set correctly

      logAuthState('SET_AUTH_USER_SUCCESS', { 
        userType: userToStore.userType,
        profileCompleted: userToStore.profileCompleted
      });
      
      return true;
    } catch (err) {
      // Handle errors
      logAuthState('SET_AUTH_USER_ERROR', { error: err.message });
      
      // Clean up any partial state
      clearAuthData();
      
      setError(err.message || 'Failed to set authenticated user');
      setIsAuthenticated(false);
      setUser(null);
      
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Login function - now supports both API credentials and direct user/token objects
  const login = useCallback(async (credentials) => {
    logAuthState('LOGIN_START', { 
      hasToken: !!(credentials.token && credentials.user),
      isApiLogin: !(credentials.token && credentials.user)
    });
    
    // If credentials contains token and user, use setAuthenticatedUser instead
    if (credentials.token && credentials.user) {
      return setAuthenticatedUser(credentials);
    }
    
    // Otherwise, proceed with API-based login
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/auth/login', credentials);
      const { token, user: userData } = response.data;
      
      if (!token || !userData) {
        throw new Error('Invalid response from server: missing token or user data');
      }
      
      // Use the setAuthenticatedUser method to handle the authentication
      const result = await setAuthenticatedUser({ token, user: userData });
      
      if (result) {
        logAuthState('LOGIN_SUCCESS', { userType: userData.userType });
      } else {
        logAuthState('LOGIN_FAILED', { error: 'setAuthenticatedUser returned false' });
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Login failed';
      logAuthState('LOGIN_ERROR', { error: errorMessage });
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [setAuthenticatedUser]);

  // Logout function
  const logout = useCallback(() => {
    logAuthState('LOGOUT_START', { user: user?.userType });
    
    // Clear all auth data
    clearAuthData();
    
    // Clear session cleanup flag to allow cleanup checks on next login
    sessionStorage.removeItem('token_cleanup_done');
    
    // Update state
    setUser(null);
    setIsAuthenticated(false);
    setAuthState({
      initialized: true,
      tokenValid: false,
      tokenExpiringSoon: false,
      lastChecked: new Date().toISOString()
    });
    
    logAuthState('LOGOUT_COMPLETE');
  }, [user]);

  // Update user profile completion status
  const updateProfileCompletion = useCallback((isCompleted, userData = null) => {
    logAuthState('UPDATE_PROFILE_START', { 
      isCompleted, 
      userType: (userData || user)?.userType 
    });
    
    try {
      // If userData is provided, use it; otherwise use the current user
      const currentUser = userData || user;
      
      if (!currentUser) {
        logAuthState('UPDATE_PROFILE_ERROR', { error: 'No user data available' });
        return null;
      }
      
      // Create updated user with profileCompleted flag explicitly set
      const updatedUser = { 
        ...currentUser, 
        profileCompleted: isCompleted === true, // Ensure it's a boolean true
        // Ensure userType is preserved
        userType: currentUser.userType || (user ? user.userType : null)
      };
      
      // Verify userType matches original
      if (user && user.userType && updatedUser.userType !== user.userType) {
        console.warn(`[WARNING] UserType mismatch during profile update: original=${user.userType}, updated=${updatedUser.userType}`);
        logAuthState('USER_TYPE_MISMATCH_UPDATE', { original: user.userType, updated: updatedUser.userType });
      }
      
      // Validate the updated user data
      const validatedUser = validateUserData(updatedUser);
      
      // Update React state
      setUser(validatedUser);
      
      // Update localStorage
      setUserData(validatedUser);
      
      logAuthState('UPDATE_PROFILE_SUCCESS', { 
        userType: validatedUser.userType,
        profileCompleted: validatedUser.profileCompleted
      });
      
      return validatedUser;
    } catch (err) {
      logAuthState('UPDATE_PROFILE_ERROR', { error: err.message });
      return null;
    }
  }, [user]);

  /**
   * Refresh the token timestamp to indicate recent activity
   */
  const refreshTokenTimestamp = useCallback(() => {
    const token = getToken();
    if (token) {
      const now = Date.now().toString();
      localStorage.setItem('authTokenCreatedAt', now);
      sessionStorage.setItem('lastUserActivity', now);
      logAuthState('TOKEN_TIMESTAMP_REFRESHED', { timestamp: now });
    }
  }, []);

  /**
   * Validates the current authentication state
   * @returns {boolean} Whether the authentication state is valid
   */
  const validateAuthState = useCallback(() => {
    logAuthState('VALIDATE_AUTH_START');
    
    if (!isAuthenticated || !user) {

      return false;
    }
    
    // Check for required user fields
    for (const field of AUTH_CONFIG.VALIDATION.REQUIRED_USER_FIELDS) {
      if (user[field] === undefined || user[field] === null) {

        logAuthState('AUTH_STATE_INVALID', { missingField: field });
        return false;
      }
    }
    
    // Validate token
    const token = getToken();
    if (!token) {

      logAuthState('AUTH_STATE_INVALID', { reason: 'missing_token' });
      return false;
    }
    
    // Verify token timestamp exists, set it if missing
    const tokenCreatedAt = localStorage.getItem('authTokenCreatedAt');
    if (!tokenCreatedAt) {
      const now = Date.now().toString();
      localStorage.setItem('authTokenCreatedAt', now);
      logAuthState('TOKEN_TIMESTAMP_MISSING_SET', { timestamp: now });
    }
    
    const tokenValidation = validateToken(token);
    if (!tokenValidation.valid) {

      logAuthState('AUTH_STATE_INVALID', { reason: 'invalid_token', error: tokenValidation.error });
      return false;
    }

    return true;
  }, [isAuthenticated, user]);
  
  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        isAuthenticated, 
        loading, 
        error, 
        login, 
        logout,
        updateProfileCompletion,
        setAuthenticatedUser,
        validateAuthState,
        refreshTokenTimestamp,
        authState
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
